import type { Location } from "react-router-dom";
import { BROWSER_ROUTES } from "@constants/browser.routes";

const MAX_REDIRECT_LEN = 2048;
const PENDING_AUTH_REDIRECT_KEY = "pendingAuthRedirect";
const POST_LOGOUT_REDIRECT_KEY = "postLogoutRedirect";
const RETURN_QUERY_KEYS = ["returnTo", "redirect", "next"] as const;

/**
 * True if pathname is a guest-only (pre-auth) route; we avoid redirecting back there after login.
 */
function isGuestAuthPathname(pathname: string): boolean {
	if (pathname.startsWith("/set-password")) return true;
	const prefixes = [
		BROWSER_ROUTES.LOGIN,
		BROWSER_ROUTES.SIGNUP,
		BROWSER_ROUTES.SIGNUP_VERIFY_OTP,
		BROWSER_ROUTES.FORGET_PASSWORD,
		BROWSER_ROUTES.VERIFY_OTP,
		BROWSER_ROUTES.RESET_PASSWORD,
	];
	return prefixes.some(p => pathname === p || pathname.startsWith(`${p}/`));
}

/**
 * Allows only same-origin relative SPA paths (no protocol / no // open redirects).
 */
export function isSafeInternalRedirect(path: string): boolean {
	if (!path || path.length > MAX_REDIRECT_LEN) return false;
	const trimmed = path.trim();
	if (!trimmed.startsWith("/")) return false;
	if (trimmed.startsWith("//")) return false;
	if (/\s/.test(trimmed)) return false;
	const pathname = trimmed.split(/[?#]/)[0] || "";
	if (!pathname) return false;
	if (isGuestAuthPathname(pathname)) return false;
	return true;
}

function parseRelativeUrl(path: string): URL | null {
	try {
		return new URL(path, "http://localhost");
	} catch {
		return null;
	}
}

function normalizeWpRedirectTarget(path: string): string {
	const parsed = parseRelativeUrl(path);
	if (!parsed) return path;

	if (parsed.searchParams.get("source") !== "wp") {
		return path;
	}

	parsed.searchParams.delete("source");
	const search = parsed.searchParams.toString();
	return `${parsed.pathname}${search ? `?${search}` : ""}${parsed.hash}`;
}

export function shouldPreserveAuthRedirect(path: string): boolean {
	if (!isSafeInternalRedirect(path)) return false;

	const parsed = parseRelativeUrl(path);
	if (!parsed) return false;

	return parsed.searchParams.get("source") === "wp";
}

export function persistPendingAuthRedirect(path: string): void {
	if (typeof window === "undefined") return;

	if (!shouldPreserveAuthRedirect(path)) {
		sessionStorage.removeItem(PENDING_AUTH_REDIRECT_KEY);
		return;
	}

	sessionStorage.setItem(PENDING_AUTH_REDIRECT_KEY, path);
}

export function syncPendingAuthRedirectFromSearch(searchParams: URLSearchParams): void {
	for (const key of RETURN_QUERY_KEYS) {
		const raw = searchParams.get(key);
		if (!raw) continue;
		persistPendingAuthRedirect(raw);
		return;
	}
}

export function getPendingAuthRedirect(): string | null {
	if (typeof window === "undefined") return null;

	const stored = sessionStorage.getItem(PENDING_AUTH_REDIRECT_KEY);
	return stored && shouldPreserveAuthRedirect(stored) ? stored : null;
}

export function clearPendingAuthRedirect(): void {
	if (typeof window === "undefined") return;
	sessionStorage.removeItem(PENDING_AUTH_REDIRECT_KEY);
}

export function resolvePendingAuthRedirect(searchParams?: URLSearchParams | null): string | null {
	if (searchParams) {
		for (const key of RETURN_QUERY_KEYS) {
			const raw = searchParams.get(key);
			if (!raw) continue;
			if (shouldPreserveAuthRedirect(raw)) {
				return raw;
			}
		}
	}

	return getPendingAuthRedirect();
}

export function buildAuthRouteWithRedirect(route: string, searchParams?: URLSearchParams | null): string {
	const pendingRedirect = resolvePendingAuthRedirect(searchParams);
	if (!pendingRedirect) return route;
	return `${route}?${new URLSearchParams({ returnTo: pendingRedirect }).toString()}`;
}

export function markPostLogoutRedirect(): void {
	if (typeof window === "undefined") return;
	// `clearAuthData()` clears sessionStorage, but we still want to bypass
	// `AuthGuard` redirect during logout. localStorage is not cleared there.
	localStorage.setItem(POST_LOGOUT_REDIRECT_KEY, "1");
}

export function consumePostLogoutRedirect(): boolean {
	if (typeof window === "undefined") return false;
	const shouldRedirectPlainly = localStorage.getItem(POST_LOGOUT_REDIRECT_KEY) === "1";
	if (shouldRedirectPlainly) {
		localStorage.removeItem(POST_LOGOUT_REDIRECT_KEY);
	}
	return shouldRedirectPlainly;
}

function pathFromLocation(from: Pick<Location, "pathname" | "search" | "hash">): string {
	return `${from.pathname}${from.search ?? ""}${from.hash ?? ""}`;
}

/**
 * Where to send the user after a successful login.
 * 1. The route they tried to open before AuthGuard sent them to sign-in (includes query/hash).
 * 2. Otherwise `returnTo` / `redirect` / `next` on the login URL (e.g. deep links from WP).
 * 3. Dashboard.
 */
export function resolvePostLoginRedirect(params: {
	attemptedLocation?: Location | null;
	searchParams: URLSearchParams;
}): string {
	const { attemptedLocation, searchParams } = params;

	if (attemptedLocation?.pathname) {
		const candidate = pathFromLocation(attemptedLocation);
		if (isSafeInternalRedirect(candidate)) {
			return normalizeWpRedirectTarget(candidate);
		}
	}

	for (const key of RETURN_QUERY_KEYS) {
		const raw = searchParams.get(key);
		if (!raw) continue;
		let decoded: string;
		try {
			decoded = decodeURIComponent(raw.trim());
		} catch {
			continue;
		}
		if (isSafeInternalRedirect(decoded)) {
			return normalizeWpRedirectTarget(decoded);
		}
	}

	const pendingRedirect = getPendingAuthRedirect();
	if (pendingRedirect) {
		return normalizeWpRedirectTarget(pendingRedirect);
	}

	return BROWSER_ROUTES.DASHBOARD;
}
